// Optional multi-company selection for CRM shell. No-op when org has zero
// companies — downstream hooks receive usesCompanies=false and behave as today.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useOrgMode } from "../hooks/useOrgMode";
import { useCompanies } from "../hooks/useCompanies";
import { useMyCrmRole } from "../hooks/useTeamMembers";
import { companyQueryParams, UNASSIGNED_COMPANY_ID } from "../utils/companyScope";

function formatCompanyName(c) {
  if (!c || c._id === UNASSIGNED_COMPANY_ID) return null;
  return (c.name || "").trim() || null;
}

const STORAGE_KEY = "crm.selectedCompanyId";

const SelectedCompanyContext = createContext({
  usesCompanies: false,
  companyId: null,
  includeUnassigned: false,
  setCompanyId: () => {},
  company: null,
  companies: [],
  showCompanySwitcher: false,
  showCompanyLabel: false,
  companyQueryParams: {},
  isLoading: false,
});

export function SelectedCompanyProvider({ children }) {
  const { user } = useAuth();
  const { isAdmin: isCrmAdmin, companyIds: membershipCompanyIds } = useMyCrmRole();
  const isOrgAdmin = user?.role === "org_admin" || user?.role === "main_admin";
  const canSeeUnassigned = isOrgAdmin || isCrmAdmin;

  const { data: orgMode, isLoading: modeLoading, isFetched: modeFetched } = useOrgMode();
  const usesCompanies = !!orgMode?.usesCompanies;

  const { data: companiesRaw = [], isLoading: companiesLoading, isFetched: companiesFetched } = useCompanies({
    enabled: usesCompanies,
  });

  const [companyId, setCompanyIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  const setCompanyId = (id) => {
    setCompanyIdState(id || null);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  };

  const companies = useMemo(() => {
    if (!usesCompanies) return [];
    // Top nav only lists companies that have at least one project assigned.
    let items = companiesRaw.filter((c) => (c.projectCount || 0) > 0);
    if (!isOrgAdmin && Array.isArray(membershipCompanyIds) && membershipCompanyIds.length > 0) {
      const allowed = new Set(membershipCompanyIds.map(String));
      items = items.filter((c) => allowed.has(String(c._id)));
    }
    if (canSeeUnassigned) {
      items.unshift({
        _id: UNASSIGNED_COMPANY_ID,
        name: "Unassigned",
        status: "active",
      });
    }
    return items;
  }, [usesCompanies, companiesRaw, canSeeUnassigned, membershipCompanyIds, isOrgAdmin]);

  const includeUnassigned = companyId === UNASSIGNED_COMPANY_ID;

  const visibleCompanies = useMemo(
    () => companies.filter((c) => c._id !== UNASSIGNED_COMPANY_ID),
    [companies]
  );

  const company = useMemo(() => {
    if (!companyId) return null;
    return companies.find((c) => String(c._id) === String(companyId)) || null;
  }, [companies, companyId]);

  const showCompanySwitcher = usesCompanies && (
    visibleCompanies.length > 1
    || (isOrgAdmin && canSeeUnassigned && visibleCompanies.length >= 1)
  );

  const memberBelongsToSelectedCompany = useMemo(() => {
    if (!company || includeUnassigned) return false;
    if (isOrgAdmin) return true;
    if (!Array.isArray(membershipCompanyIds) || membershipCompanyIds.length === 0) {
      return true;
    }
    return membershipCompanyIds.map(String).includes(String(company._id));
  }, [company, includeUnassigned, membershipCompanyIds, isOrgAdmin]);

  const showCompanyLabel = usesCompanies
    && !showCompanySwitcher
    && memberBelongsToSelectedCompany
    && !!formatCompanyName(company);

  // Auto-select when user has exactly one real company (no switcher for them).
  useEffect(() => {
    if (!usesCompanies || !companiesFetched) return;

    if (visibleCompanies.length === 0) {
      if (companyId) setCompanyId(null);
      return;
    }

    const valid = companies.some((c) => String(c._id) === String(companyId));

    if (visibleCompanies.length === 1) {
      const only = String(visibleCompanies[0]._id);
      // Org admin keeps switcher (company ↔ Unassigned) — don't override Unassigned.
      if (!isOrgAdmin) {
        if (companyId !== only) setCompanyId(only);
        return;
      }
      if (!companyId || !valid) setCompanyId(only);
      return;
    }

    // 2+ companies: ensure stored id is still valid (or unassigned for admins).
    if (!companyId || !valid) {
      setCompanyId(String(visibleCompanies[0]._id));
    }
  }, [usesCompanies, companiesFetched, visibleCompanies, companies, companyId, isOrgAdmin]);

  const isResolving = modeLoading || !modeFetched || (usesCompanies && (companiesLoading || !companiesFetched));

  const queryParams = useMemo(
    () => companyQueryParams({ usesCompanies, companyId, includeUnassigned }),
    [usesCompanies, companyId, includeUnassigned]
  );

  const value = useMemo(() => ({
    usesCompanies,
    companyId: usesCompanies ? companyId : null,
    includeUnassigned,
    setCompanyId,
    company,
    companies: usesCompanies ? companies : [],
    showCompanySwitcher,
    showCompanyLabel,
    companyQueryParams: queryParams,
    isLoading: isResolving,
    isOrgAdmin,
  }), [
    usesCompanies, companyId, includeUnassigned, company, companies,
    showCompanySwitcher, showCompanyLabel, queryParams, isResolving, isOrgAdmin,
  ]);

  return (
    <SelectedCompanyContext.Provider value={value}>
      {children}
    </SelectedCompanyContext.Provider>
  );
}

export function useSelectedCompany() {
  return useContext(SelectedCompanyContext);
}
